/**
 * Driver duty on GPS (N-PAC, 28 Oct 2026: "गाडी मैनेजमेंट में भी ड्राइवर के जीपीएस से एटैच रहें").
 *
 * The driver opens the vehicle's link and presses "start duty"; while the duty is on, the phone keeps
 * a GPS fix every 15-60 s (more often when moving), queues the points on the phone (they survive a
 * reload and no network) and sends them in batches. The server counts the km and the places reached.
 * Nothing is recorded outside a duty.
 *
 * Plain TypeScript (no React) with the browser pieces injected, so it can be tested in Node.
 */
import { Emitter } from "@core/emitter";
import type { DrivePoint, DrivePointsReply, DriveSession, FieldApi } from "./api";

export interface KeyValue {
  get(k: string): string | null;
  set(k: string, v: string | null): void;
}

export interface GeoLike {
  watchPosition(ok: (p: { coords: { latitude: number; longitude: number; accuracy: number; speed: number | null }; timestamp: number }) => void, err: (e: { code: number }) => void, opts?: object): number;
  clearWatch(id: number): void;
}

export interface TrackerDeps {
  api: FieldApi;
  assemblyId: () => string | null;
  geo: GeoLike | null;
  kv: KeyValue;
  makeId: () => string;
  now?: () => number;
  online?: () => boolean;
  timers?: { set: (fn: () => void, ms: number) => unknown; clear: (h: unknown) => void };
  /** a 401: the link was changed — end the phone's session */
  onUnauthenticated?: (e: unknown) => void;
}

export interface TrackerState {
  /** the duty on the server (null = off duty) */
  session: DriveSession | null;
  watching: boolean;
  gpsError: "denied" | "unavailable" | null;
  lastFix: { lat: number; lng: number; accuracyM: number; at: number } | null;
  queued: number;
  lastSentAt: number | null;
  sending: boolean;
  /** places / booths reached (from the server), newest first, this duty */
  reached: { target: "place" | "booth"; id: number; name: string; at: number }[];
}

const K_ACTIVE = "drive.active";
const kQueue = (sid: number) => `drive.q.${sid}`;
const BATCH = 200;
const SEND_EVERY_MS = 20_000;

/** Keep a fix if enough time passed and the vehicle moved, or a minute passed, or it moved far. */
export function shouldRecord(prev: { lat: number; lng: number; at: number } | null, next: { lat: number; lng: number; at: number; accuracyM: number }): boolean {
  if (next.accuracyM > 500) return false;
  if (!prev) return true;
  const dt = (next.at - prev.at) / 1000;
  const dLat = (next.lat - prev.lat) * 111_320;
  const dLng = (next.lng - prev.lng) * 111_320 * Math.cos((prev.lat * Math.PI) / 180);
  const d = Math.hypot(dLat, dLng);
  return (dt >= 15 && (d >= 25 || dt >= 60)) || (dt >= 5 && d >= 150);
}

export class DriveTracker {
  readonly changes = new Emitter();
  state: TrackerState = { session: null, watching: false, gpsError: null, lastFix: null, queued: 0, lastSentAt: null, sending: false, reached: [] };
  private watchId: number | null = null;
  private timer: unknown = null;
  private lastKept: { lat: number; lng: number; at: number } | null = null;
  private readonly now: () => number;

  constructor(private readonly d: TrackerDeps) {
    this.now = d.now ?? Date.now;
  }

  private patch(p: Partial<TrackerState>) {
    this.state = { ...this.state, ...p };
    this.changes.emit();
  }

  private queue(sid: number): DrivePoint[] {
    try {
      return JSON.parse(this.d.kv.get(kQueue(sid)) ?? "[]") as DrivePoint[];
    } catch {
      return [];
    }
  }
  private saveQueue(sid: number, q: DrivePoint[]) {
    this.d.kv.set(kQueue(sid), q.length ? JSON.stringify(q) : null);
    this.patch({ queued: q.length });
  }

  /** The server's view after loading the screen: an open duty resumes tracking on this phone. */
  adopt(session: DriveSession | null) {
    if (session && !session.endedAt) {
      this.d.kv.set(K_ACTIVE, JSON.stringify({ id: session.id, clientId: session.clientId }));
      this.patch({ session, queued: this.queue(session.id).length });
      this.watch();
    } else if (!this.state.session) {
      // a duty that ended elsewhere (admin, new link): send what is left, then stop
      const a = this.active();
      if (a) void this.flush(a.id);
    }
  }

  private active(): { id: number; clientId: string } | null {
    try {
      const v = this.d.kv.get(K_ACTIVE);
      return v ? JSON.parse(v) : null;
    } catch {
      return null;
    }
  }

  /** "Start duty": one id per press, so a retry on a bad network resumes the same duty. */
  async start(): Promise<DriveSession> {
    const aid = this.d.assemblyId();
    if (!aid) throw new Error("No assembly");
    const pending = this.d.kv.get("drive.pendingStart") ?? this.d.makeId();
    this.d.kv.set("drive.pendingStart", pending);
    const r = await this.d.api.driveStart(aid, pending);
    this.d.kv.set("drive.pendingStart", null);
    this.d.kv.set(K_ACTIVE, JSON.stringify({ id: r.session.id, clientId: r.session.clientId }));
    this.lastKept = null;
    this.patch({ session: r.session, reached: [], queued: this.queue(r.session.id).length });
    this.watch();
    return r.session;
  }

  /** "End duty": stop GPS, send what is queued, close the duty on the server. */
  async end(): Promise<DriveSession | null> {
    const s = this.state.session;
    this.stopWatch();
    if (!s) return null;
    await this.flush(s.id).catch(() => undefined);
    const aid = this.d.assemblyId();
    const r = aid ? await this.d.api.driveEnd(aid, s.id) : null;
    if (!this.queue(s.id).length) this.d.kv.set(K_ACTIVE, null);
    this.patch({ session: null });
    return r?.session ?? null;
  }

  private watch() {
    if (this.watchId != null || !this.d.geo) {
      if (!this.d.geo) this.patch({ gpsError: "unavailable" });
      return;
    }
    this.watchId = this.d.geo.watchPosition(
      (p) => this.onFix({ lat: p.coords.latitude, lng: p.coords.longitude, accuracyM: Math.round(p.coords.accuracy), at: p.timestamp || this.now(), speedMs: p.coords.speed }),
      (e) => this.patch({ gpsError: e.code === 1 ? "denied" : "unavailable" }),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 }
    );
    this.patch({ watching: true });
    this.schedule();
  }

  private stopWatch() {
    if (this.watchId != null && this.d.geo) this.d.geo.clearWatch(this.watchId);
    this.watchId = null;
    if (this.timer != null) (this.d.timers?.clear ?? clearTimeout)(this.timer as never);
    this.timer = null;
    this.patch({ watching: false });
  }

  private schedule() {
    const set = this.d.timers?.set ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
    this.timer = set(() => {
      this.timer = null;
      const s = this.state.session;
      if (s) void this.flush(s.id).finally(() => this.watchId != null && this.schedule());
    }, SEND_EVERY_MS);
  }

  /** a GPS fix from the phone */
  onFix(f: { lat: number; lng: number; accuracyM: number; at: number; speedMs?: number | null }) {
    const s = this.state.session;
    this.patch({ lastFix: { lat: f.lat, lng: f.lng, accuracyM: f.accuracyM, at: f.at }, gpsError: null });
    if (!s || !shouldRecord(this.lastKept, f)) return;
    let speed = f.speedMs != null && f.speedMs >= 0 ? f.speedMs * 3.6 : null;
    if (speed == null && this.lastKept) {
      const dt = (f.at - this.lastKept.at) / 1000;
      const d = Math.hypot((f.lat - this.lastKept.lat) * 111_320, (f.lng - this.lastKept.lng) * 111_320 * Math.cos((f.lat * Math.PI) / 180));
      speed = dt > 0 ? (d / dt) * 3.6 : null;
    }
    this.lastKept = { lat: f.lat, lng: f.lng, at: f.at };
    const q = this.queue(s.id);
    q.push({ id: this.d.makeId(), at: new Date(f.at).toISOString(), lat: Math.round(f.lat * 1e6) / 1e6, lng: Math.round(f.lng * 1e6) / 1e6, acc: f.accuracyM, speed: speed != null ? Math.round(speed * 10) / 10 : null });
    this.saveQueue(s.id, q);
    // send on the fix itself too: in the background (the APK with the screen locked) timers are slowed
    // down, the plugin's fixes keep coming
    if (q.length >= 10 || this.now() - (this.state.lastSentAt ?? 0) >= 60_000) void this.flush(s.id);
  }

  /** Send queued points (up to BATCH at a time); a sent point leaves the queue only after the server took it. */
  async flush(sid: number): Promise<void> {
    if (this.state.sending) return;
    const aid = this.d.assemblyId();
    if (!aid || (this.d.online && !this.d.online())) return;
    this.patch({ sending: true });
    try {
      for (;;) {
        const q = this.queue(sid);
        if (!q.length) break;
        const batch = q.slice(0, BATCH);
        let r: DrivePointsReply;
        try {
          r = await this.d.api.drivePoints(aid, sid, batch);
        } catch (e) {
          if ((e as { status?: number })?.status === 401) this.d.onUnauthenticated?.(e);
          // a duty the server no longer has: nothing to send it to
          if ((e as { status?: number })?.status === 404) this.saveQueue(sid, []);
          break;
        }
        const sent = new Set(batch.map((p) => p.id));
        this.saveQueue(sid, this.queue(sid).filter((p) => !sent.has(p.id)));
        const at = this.now();
        this.patch({
          lastSentAt: at,
          session: this.state.session && this.state.session.id === sid ? { ...this.state.session, ...r.session } : this.state.session,
          reached: [...r.reached.map((x) => ({ ...x, at })), ...this.state.reached].slice(0, 50),
        });
      }
    } finally {
      this.patch({ sending: false });
      if (!this.state.session && !this.queue(sid).length) this.d.kv.set(K_ACTIVE, null);
    }
  }

  dispose() {
    this.stopWatch();
  }
}
